import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import gtmService from '../services/gtmService';
import {
  Typography,
  Box,
  TextField,
  Button,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  Container,
  IconButton,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Link
} from '@mui/material';
import {
  Person as PersonIcon,
  Lock as LockIcon,
  Login as LoginIcon,
  Visibility,
  VisibilityOff,
  PersonAdd as PersonAddIcon,
  WhatsApp as WhatsAppIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import authService from '../services/authService';
import ForgotPasswordLink from './ForgotPasswordLink';

const Login = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Trim whitespace and validate
    const trimmedUsername = username.trim();
    const trimmedPassword = password.trim();
    
    if (!trimmedUsername || !trimmedPassword) {
      setError('يرجى إدخال اسم المستخدم وكلمة المرور');
      return;
    }

    setError('');
    setSuccess('');
    setLoading(true);
    
    try {
      // Use JWT authentication service
      const result = await authService.login(trimmedUsername, trimmedPassword);
      
      if (result.success && result.user) {
        
        // Store user data in localStorage for backward compatibility
        localStorage.setItem('user', JSON.stringify(result.user));
        localStorage.setItem('isCustomer', 'true');
        localStorage.setItem('isLoggedIn', 'true');
        
        // Clean up old keys
        ["name", "username", "phone", "city", "address", "points", "role"].forEach(key => localStorage.removeItem(key));
        
        // Dispatch custom event to notify other components of user login
        window.dispatchEvent(new CustomEvent('userUpdated'));
        
        // Track login with GTM
        gtmService.trackLogin('email');
        
        setSuccess('تم تسجيل الدخول بنجاح! جاري التوجيه...');
        
        setTimeout(() => {
          // Check user role and redirect accordingly
          if (result.user.role === 'whole') {
            navigate('/wholesale');
          } else {
            navigate('/');
          }
        }, 1000);
      } else {
        setError('فشل في تسجيل الدخول. يرجى المحاولة مرة أخرى.');
      }
    } catch (error) {
      if (error.message.includes('Invalid credentials') || error.message.includes('Authentication failed')) {
        setError('اسم المستخدم أو كلمة المرور غير صحيحة');
      } else if (error.message.includes('Network')) {
        setError('خطأ في الاتصال بالخادم. يرجى المحاولة لاحقاً');
      } else {
        setError('حدث خطأ في تسجيل الدخول. يرجى المحاولة مرة أخرى');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleTogglePassword = () => {
    setShowPassword(!showPassword);
  };

  const handleRegisterClick = () => {
    navigate('/register');
  };

  return (
    <Box sx={{ 
      minHeight: '100vh',
      backgroundColor: '#ffffff',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      py: 4
    }}>
      <Container maxWidth="sm">
        {/* Main Login Card */}
        <Card sx={{ 
          backdropFilter: 'blur(20px)',
          background: 'rgba(255, 255, 255, 0.95)',
          borderRadius: 4,
          boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          overflow: 'hidden'
        }}>
          {/* Header Section */}
          <Box sx={{
            background: 'linear-gradient(135deg, #3b5dac 0%, #667eea 100%)',
            color: 'white',
            p: 4,
            textAlign: 'center'
          }}>
            <Box sx={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              background: 'rgba(255, 255, 255, 0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 3
            }}>
              <LoginIcon sx={{ fontSize: 40, color: 'white' }} />
            </Box>
            <Typography variant="h4" sx={{ 
              fontWeight: 'bold', 
              mb: 1,
              fontSize: { xs: '1.8rem', md: '2.2rem' }
            }}>
              مرحباً بعودتك
            </Typography>
            <Typography variant="body1" sx={{ 
              opacity: 0.9,
              fontSize: '1.1rem'
            }}>
              سجل دخولك للوصول إلى حسابك
            </Typography>
          </Box>

          <CardContent sx={{ p: 4 }}>
            <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* Username Field */}
              <Box>
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 'medium', color: '#333' }}>
                  رقم الموبايل
                </Typography>
                <TextField
                  fullWidth
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonIcon sx={{ color: '#3b5dac' }} />
                      </InputAdornment>
                    )
                  }}
                  placeholder="07701234567"
                  variant="outlined"
                  required
                  disabled={loading}
                  autoComplete="username"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      backgroundColor: '#f8f9fa',
                      '& fieldset': {
                        borderColor: 'transparent',
                      },
                      '&:hover fieldset': {
                        borderColor: '#3b5dac',
                      },
                      '&.Mui-focused': {
                        backgroundColor: 'white',
                        '& fieldset': {
                          borderColor: '#3b5dac',
                          borderWidth: 2,
                        },
                      },
                    },
                  }}
                />
              </Box>
              
              {/* Password Field */}
              <Box>
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 'medium', color: '#333' }}>
                  كلمة المرور
                </Typography>
                <TextField
                  fullWidth
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockIcon sx={{ color: '#3b5dac' }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={handleTogglePassword}
                          edge="end"
                          disabled={loading}
                          sx={{ color: '#3b5dac' }}
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                  placeholder="أدخل كلمة المرور"
                  variant="outlined"
                  required
                  disabled={loading}
                  autoComplete="current-password"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      backgroundColor: '#f8f9fa',
                      '& fieldset': {
                        borderColor: 'transparent',
                      },
                      '&:hover fieldset': {
                        borderColor: '#3b5dac',
                      },
                      '&.Mui-focused': {
                        backgroundColor: 'white',
                        '& fieldset': {
                          borderColor: '#3b5dac',
                          borderWidth: 2,
                        },
                      },
                    },
                  }}
                />
              </Box>

              {/* Forgot Password Link */}
              <Box sx={{ textAlign: 'right' }}>
                <ForgotPasswordLink 
                  onSuccess={() => {
                    setSuccess('تم تغيير كلمة المرور بنجاح! يمكنك تسجيل الدخول الآن');
                    setError('');
                  }}
                  sx={{
                    color: '#3b5dac',
                    textDecoration: 'none',
                    fontWeight: 'medium',
                    '&:hover': {
                      textDecoration: 'underline',
                    },
                  }}
                />
              </Box>

              {/* Error Message */}
              {error && (
                <Alert 
                  severity="error" 
                  sx={{ 
                    borderRadius: 2,
                    backgroundColor: '#fff5f5',
                    border: '1px solid #fed7d7',
                    '& .MuiAlert-icon': {
                      color: '#e53e3e'
                    }
                  }}
                >
                  {error}
                </Alert>
              )}

              {/* Success Message */}
              {success && (
                <Alert 
                  severity="success" 
                  sx={{ 
                    borderRadius: 2,
                    backgroundColor: '#f0fff4',
                    border: '1px solid #9ae6b4',
                    '& .MuiAlert-icon': {
                      color: '#38a169'
                    }
                  }}
                >
                  {success}
                </Alert>
              )}

              {/* Login Button */}
              <Button
                fullWidth
                variant="contained"
                size="large"
                type="submit"
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <LoginIcon />}
                sx={{
                  background: 'linear-gradient(135deg, #3b5dac 0%, #667eea 100%)',
                  py: 2,
                  fontSize: '1.1rem',
                  fontWeight: 'bold',
                  borderRadius: 2,
                  boxShadow: '0 8px 20px rgba(59, 93, 172, 0.3)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #2a406e 0%, #5a6fd8 100%)',
                    boxShadow: '0 12px 25px rgba(59, 93, 172, 0.4)',
                    transform: 'translateY(-2px)',
                  },
                  '&:active': {
                    transform: 'translateY(0)',
                  },
                  transition: 'all 0.3s ease',
                }}
              >
                {loading ? 'جارٍ تسجيل الدخول...' : 'تسجيل الدخول'}
              </Button>

              {/* Divider */}
              <Box sx={{ position: 'relative', my: 2 }}>
                <Divider sx={{ 
                  '&::before, &::after': {
                    borderColor: 'rgba(0, 0, 0, 0.1)',
                  }
                }}>
                  <Typography variant="body2" sx={{ 
                    color: 'text.secondary', 
                    px: 2,
                    backgroundColor: 'white'
                  }}>
                    أو
                  </Typography>
                </Divider>
              </Box>

              {/* Register Button */}
              <Button
                fullWidth
                variant="outlined"
                size="large"
                onClick={handleRegisterClick}
                startIcon={<PersonAddIcon />}
                sx={{
                  borderColor: '#ffffffff',
                  color: '#ffffffff',
                  py: 2,
                  fontSize: '1rem',
                  fontWeight: 'bold',
                  borderRadius: 2,
                  borderWidth: 2,
                  '&:hover': {
                    borderColor: '#28a745',
                    backgroundColor: '#28a745',
                    color: 'white',
                    borderWidth: 2,
                    transform: 'translateY(-2px)',
                    boxShadow: '0 8px 20px rgba(255, 255, 255, 1)',
                  },
                  '&:active': {
                    transform: 'translateY(0)',
                  },
                  transition: 'all 0.3s ease',
                }}
              >
                  إنشاء حساب جديد  
              </Button>
            </Box>
          </CardContent>
        </Card>

        {/* Footer */}
        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <Typography variant="body2" sx={{ 
            color: 'text.secondary',
            fontSize: '0.9rem'
          }}>
            جميع الحقوق محفوظة © 2025
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default Login;
